import { useCallback } from 'react';
import { MutationResponse } from 'global';
import { useApolloClient, useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { TableFilter } from '@trnsact/trnsact-shared-types';

import { opportunitiesListApi } from './index';
import { formatTableFiltersForPayload } from '../lib';

type CreateTableFilterDto = Pick<TableFilter, 'name' | 'config' | 'isDefault' | 'tableType'>;
type UpdateTableFilterDto = Pick<TableFilter, 'name' | 'config' | 'tableFilterId'>;

export function usePresets() {
  const client = useApolloClient();
  const { showNotification } = useNotifications();

  const [onCreate, { loading: isFilterCreating }] = useMutation<
    {
      tableFilter: MutationResponse<void>;
    },
    {
      input: CreateTableFilterDto;
    }
  >(opportunitiesListApi.createFilter);

  const handleCreateFilter = useCallback(
    async (dto: CreateTableFilterDto) => {
      try {
        const input = formatTableFiltersForPayload(dto);
        const { data } = await onCreate({ variables: { input } });

        if (!data?.tableFilter?.result?.success) {
          showNotification(data?.tableFilter?.result?.error ?? 'Filter not created', { type: 'error' });
          return;
        }

        await client.refetchQueries({ include: [opportunitiesListApi.getTableFilters] });
        showNotification('Filter successfully created!');
      } catch (error: any) {
        showNotification(error?.message ?? 'Filter not created', { type: 'error' });
      }
    },
    [onCreate, client, showNotification]
  );

  const [onUpdate, { loading: isFilterUpdating }] = useMutation<
    {
      tableFilter: MutationResponse<void>;
    },
    {
      input: UpdateTableFilterDto;
    }
  >(opportunitiesListApi.updateFilter);

  const handleUpdateFilter = useCallback(
    async (dto: UpdateTableFilterDto) => {
      try {
        const input = formatTableFiltersForPayload(dto);
        const { data } = await onUpdate({ variables: { input } });

        if (!data?.tableFilter?.result?.success) {
          showNotification(data?.tableFilter?.result?.error ?? 'Filter not updated', { type: 'error' });
          return;
        }

        await client.refetchQueries({ include: [opportunitiesListApi.getTableFilters] });
        showNotification('Filter successfully updated!');
      } catch (error: any) {
        showNotification(error?.message ?? 'Filter not updated', { type: 'error' });
      }
    },
    [onUpdate, client, showNotification]
  );

  const [onDelete, { loading: isFilterDeleting }] = useMutation<{ tableFilter: MutationResponse<void> }>(
    opportunitiesListApi.deleteFilter
  );

  const handleDeleteFilter = useCallback(
    async (id: string) => {
      try {
        const result = await onDelete({ variables: { tableFilterId: id } });

        if (result?.data?.tableFilter?.result?.success) {
          await client.refetchQueries({ include: [opportunitiesListApi.getTableFilters] });
          showNotification('Filter successfully deleted!');
        } else {
          showNotification('Filter not deleted', { type: 'error' });
        }

        return result?.data?.tableFilter?.result;
      } catch (error: any) {
        showNotification(error?.message ?? 'Filter not deleted', { type: 'error' });
      }
    },
    [onDelete, client, showNotification]
  );

  return {
    handleCreateFilter,
    isFilterCreating,
    handleUpdateFilter,
    isFilterUpdating,
    handleDeleteFilter,
    isFilterDeleting,
  };
}
